import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save, Loader2, Key } from 'lucide-react';
import api from '@/lib/api';

export default function AdminOAuthSettings() {
    const [settings, setSettings] = useState([]);
    const [values, setValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/site-settings');
            const data = (res.data.data || []).filter(s => s.group === 'oauth');
            setSettings(data);
            const initialValues = {};
            data.forEach(s => { initialValues[s.key] = s.value || ''; });
            setValues(initialValues);
        } catch {
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setSaved(false);
        try {
            const payload = Object.keys(values).map(key => ({ key, value: values[key] }));
            await api.put('/admin/site-settings', { settings: payload });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch {
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <h1 className="text-2xl font-bold uppercase tracking-wide">OAuth</h1>
                <p className="text-white/60 text-sm mt-1">Configuración de inicio de sesión con Google.</p>
            </div>

            <Card className="border-hairline-dark bg-[#0a0a0a]">
                <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        Google OAuth
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                    {settings.length === 0 && (
                        <p className="text-sm text-white/40">No hay configuraciones de OAuth disponibles. Creá las entradas en la base de datos con el grupo "oauth".</p>
                    )}
                    {settings.map(setting => (
                        <div key={setting.key} className="space-y-1.5">
                            <label className="flex items-center gap-2 text-xs text-white/60 uppercase tracking-wider">
                                <Key className="h-4 w-4" />
                                {setting.label}
                            </label>
                            <Input
                                value={values[setting.key] || ''}
                                onChange={(e) => setValues(prev => ({ ...prev, [setting.key]: e.target.value }))}
                                type={setting.key.includes('secret') ? 'password' : 'text'}
                                placeholder={setting.description}
                                className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                            />
                            <p className="text-[10px] text-white/40">{setting.description}</p>
                        </div>
                    ))}
                </CardContent>
            </Card>

            <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full uppercase tracking-widest text-xs max-w-3xl"
            >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                Guardar Cambios
            </Button>
            {saved && (
                <p className="text-center text-xs text-green-400 mt-2">Configuración guardada correctamente.</p>
            )}
        </div>
    );
}
